import React, { createContext, useContext, ReactNode, useEffect } from 'react'
import { setLocale, getSsrLocale, initLocale, t, Locale, TranslationMap } from '../i18n/translator'

interface I18nContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: typeof t
}

const I18nContext = createContext<I18nContextValue>({
  locale: 'en',
  setLocale,
  t,
})

interface Props {
  children: ReactNode
  locale?: Locale
}

export const I18nProvider = ({ children, locale }: Props) => {
  const initialized = initLocale(locale)

  useEffect(() => {
    const fromClient = typeof window !== 'undefined'
      ? (() => {
          try {
            const url = new URL(window.location.href)
            const qp = url.searchParams.get('lang')
            if (qp && /^[a-z]{2}$/i.test(qp)) return qp.toLowerCase() as Locale
            const ls = window.localStorage.getItem('lang')
            if (ls && /^[a-z]{2}$/i.test(ls)) return ls.toLowerCase() as Locale
            const nav = (navigator.language || 'en').slice(0, 2).toLowerCase() as Locale
            return nav
          } catch {
            return 'en'
          }
        })()
      : undefined
    if (fromClient) setLocale(fromClient)
  }, [])

  return (
    <I18nContext.Provider value={{ locale: initialized, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  )
}

export const useI18n = () => useContext(I18nContext)
